import { KeyboardEvent as ReactKeyboardEvent, useEffect, useId, useRef, useState } from "react";
import styled from "styled-components";

import { ListCardAction } from "../ListCard";

/* The "⋯" button and its menu, anchored to it */
const ActionsMenu = styled.div`
  position: relative;

  [role="menu"] {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 20;
    display: flex;
    flex-direction: column;
    min-width: 220px;
    padding: 6px;
    background-color: var(--color-neutral-dark);
    border: 1px solid var(--color-neutral-base);
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  }

  [role="menuitem"] {
    padding: 8px 10px;
    font-family: Inter, sans-serif;
    font-size: 0.8125rem;
    text-align: left;
    color: var(--color-neutral-white);
    border-radius: 8px;

    &:hover:not(:disabled),
    &:focus-visible {
      background-color: var(--color-neutral-base);
    }

    &:disabled {
      cursor: default;
      opacity: 0.4;
    }

    &.danger {
      color: var(--color-feedback-danger);
    }
  }
`;

const DotsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="5" cy="12" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="19" cy="12" r="2" />
  </svg>
);

export type RowAction = { label: string; onSelect: () => void; disabled?: boolean; danger?: boolean };

/**
 * A row's "⋯" (a page's, a section's): its actions in a small menu; closes on a pick, Escape or a click elsewhere.
 * The row it's in opens on a click: clicks here stay here. The row needs its own stacking layer above the next
 * ones while the menu is open (`&:has([role="menu"]) { z-index }`), or they cover it.
 */
export const RowActions = ({ label, actions }: { label: string; actions: RowAction[] }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const menuId = useId();

  /** The items that can be reached (a disabled one can't be focused). */
  const items = () => [
    ...(menu.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? []),
  ];

  /** Closes it, the focus back on the "⋯" it came from (not lost on a button that's gone). */
  const close = () => {
    setOpen(false);
    trigger.current?.focus();
  };

  // opening it puts the focus on its first item
  useEffect(() => {
    if (open) items()[0]?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
      }
    };
    const onPointer = (event: MouseEvent) => !ref.current?.contains(event.target as Node) && setOpen(false);
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [open]);

  const onMenuKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const reachable = items();
    const at = reachable.indexOf(document.activeElement as HTMLButtonElement);
    const to = {
      ArrowDown: (at + 1) % reachable.length,
      ArrowUp: (at - 1 + reachable.length) % reachable.length,
      Home: 0,
      End: reachable.length - 1,
    }[event.key];
    if (to !== undefined && reachable.length) {
      event.preventDefault();
      reachable[to].focus();
    } else if (event.key === "Tab") {
      // the focus leaves the menu: it closes
      setOpen(false);
    }
  };

  return (
    <ActionsMenu ref={ref} onClick={event => event.stopPropagation()}>
      <ListCardAction
        ref={trigger}
        type="button"
        title={label}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen(!open)}>
        <DotsIcon />
      </ListCardAction>
      {open && (
        <div role="menu" id={menuId} aria-label={label} ref={menu} onKeyDown={onMenuKey}>
          {actions.map(({ label: name, onSelect, disabled, danger }) => (
            <button
              key={name}
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={danger ? "danger" : undefined}
              disabled={disabled}
              onClick={() => {
                close();
                onSelect();
              }}>
              {name}
            </button>
          ))}
        </div>
      )}
    </ActionsMenu>
  );
};
